<template>
  <div class="r-form">
    <vue-element-loading v-if="!hideLoading" :active="loading"></vue-element-loading>
    <div class="form-header" v-if="showHeader">
      <slot name="title">
        {{ title }}
      </slot>
    </div>
    <b-form @submit.prevent="onSubmit">
      <div class="form-body container-fluid">
        <slot></slot>
        <div
                class="read-only-fields"
                v-if="readOnly && !readOnlyDisabled"
        >
          <b-row>
            <template v-for="(field, key) in fields">
              <b-col
                  cols="12"
                  v-bind="{...columnOptions, ...field.columnOptions}"
                  v-show="!isFieldHidden(field, fields)"
              >
                <b-form-group
                        :label="field.label"
                        label-size="sm"
                >
                  <div class="b-form-group-content">
                    <b-form-checkbox
                            :unchecked-value="0"
                            :value="1"
                            disabled
                            v-if="fieldTypes.checkbox.includes(field.type)"
                            v-model="$v.fields[key].value.$model"
                    ></b-form-checkbox>
                    <template v-else-if="fieldTypes.multiselect.includes(field.type) && field.multiple">
                      <b-badge :key="item.value"
                               pill
                               tag="div"
                               class="mr-1"
                               v-for="item in field.value"
                               variant="primary"
                      >
                        {{ item.value }}
                      </b-badge>
                    </template>
                    <template v-else>
                      {{ getFieldValue(field) }}
                    </template>
                  </div>
                </b-form-group>
              </b-col>
            </template>
          </b-row>
        </div>
          <b-row v-else>
            <template v-for="(field, key) in fields">
              <div v-if="field.group" class="col-md-12 col-12">
                <div class="group-header col-md-6 col-12">
                  {{ field.group }}
                </div>
              </div>
              <b-col
                  :key="key"
                  cols="12"
                  v-bind="{...columnOptions, ...field.columnOptions}"
                  v-show="!isFieldHidden(field, fields)"
              >
              <div :class="computeColClass(field)">
                <b-form-group
                        v-if="!fieldTypes.buttons.includes(field.type)"
                        :label="field.label"
                        label-size="sm"
                        :description="field.description || ''"
                >
                  <b-form-input
                          v-if="fieldTypes.input.includes(field.type)"
                          v-show="field.hidden ? !field.hidden(fields) : true"
                          :type="field.type"
                          :placeholder="field.placeholder"
                          v-model="$v.fields[key].value.$model"
                          :disabled="field.disabled || readOnlyDisabled"
                          :state="!(field.disabled || readOnlyDisabled) ? getState($v.fields[key]) : null"
                  ></b-form-input>
                  <date-picker
                          v-else-if="fieldTypes.date.includes(field.type)"
                          v-show="field.hidden ? !field.hidden(fields) : true"
                          ref="datePicker"
                          :disabled="field.disabled || readOnlyDisabled"
                          v-model="$v.fields[key].value.$model"
                          :target-date="$v.fields[key].value ? $v.fields[key].value.$model : null"
                          :confirm-buttons="field.confirmButtons ? field.confirmButtons : false"
                          :reduceButtons="field.reduceButtons ? field.reduceButtons : false"
                  >
                  </date-picker>
                  <b-form-textarea
                          v-else-if="fieldTypes.textarea.includes(field.type)"
                          :type="field.type"
                          :placeholder="field.placeholder"
                          v-model="$v.fields[key].value.$model"
                          :disabled="field.disabled || readOnlyDisabled"
                          :state="!(field.disabled || readOnlyDisabled) ? getState($v.fields[key]) : null"
                          rows="2"
                  ></b-form-textarea>
                  <b-form-checkbox
                          v-else-if="fieldTypes.checkbox.includes(field.type)"
                          v-model="$v.fields[key].value.$model"
                          :disabled="field.disabled || readOnlyDisabled"
                          :unchecked-value="0"
                          :value="1"
                  ></b-form-checkbox>
                  <multiselect
                          v-else-if="fieldTypes.multiselect.includes(field.type)"
                          :options="field.options"
                          v-model="field.value"
                          :label="field.labelBy"
                          :track-by="field.trackBy"
                          :multiple="field.multiple"
                          :placeholder="field.placeholder"
                          :hide-selected="true"
                          :disabled="field.disabled || readOnlyDisabled"
                          :openDirection="field.openDirection || 'bottom'"
                          :class="{
                              'multiselect-is-invalid': hasValidationError(key, 'requiredIf'),
                              'multiselect-is-valid': !hasValidationError(key, 'requiredIf') && field.value && fieldInteracted[key]
                          }"
                          selectLabel=""
                          tagPlaceholder=" "
                          deselectLabel=""
                          @input="markFieldAsInteracted(key); $emit('select:changed', {field, key})"
                  >
                    <template slot="clear" v-if="field.value && !(field.disabled || readOnlyDisabled)">
                      <div
                         class="clear-select"
                      >
                      <font-awesome-icon
                          @mousedown.prevent.stop="field.value = null"
                          :icon="['far', 'times']">
                      </font-awesome-icon>
                      </div>
                    </template>
                    <template #caret="{ toggle }">
                      <caret-icon @mousedown.prevent.stop="toggle"></caret-icon>
                    </template>
                  </multiselect>
                  <file-drop
                          v-else-if="fieldTypes.file.includes(field.type)"
                          :fileUrl="field.fileUrl"
                          :id="field.id"
                          :ref="field.id + '-dropzone'"
                          :options="field.options"
                          :additionalParams="field.additionalParams(fields)"
                          @onFileSuccess="$emit('onFileSuccess')"
                          @onFileError="$emit('onFileError')"

                  >
                  </file-drop>
                  <form-error
                          v-if="hasValidationError(key, 'required')
                            || hasValidationError(key, 'requiredIf')
                          "
                  >
                    This field is required
                  </form-error>
                  <form-error v-if="hasValidationError(key, 'email')">
                    Please enter a valid e-mail
                  </form-error>
                  <form-error v-if="hasValidationError(key, 'postcode')">
                    Please enter a valid postcode
                  </form-error>
                  <div v-for="(message) in field.messages"
                       v-if="field.messages && message.show(fields)"
                       class="pt-2 messages"
                  >
                    <div :class="`alert alert-${message.type}`">{{message.text}}</div>
                  </div>
                </b-form-group>
                <b-form-group v-else-if="fieldTypes.buttons.includes(field.type)">
                  <b-button

                          :disabled="field.disabled"
                          @click="field.onClick"
                  >
                    {{ field.label }}
                    <font-awesome-icon v-if="field.icon" :icon="field.icon"></font-awesome-icon>
                  </b-button>
                </b-form-group>
              </div>
              </b-col>
            </template>
          </b-row>
        </div>

        <div class="form-footer fixed-bottom" v-if="showFooter">
            <slot
                    :form="fields"
                    :valid="$v.fields.$invalid"
                    name="buttons"
            >
          <template v-if="readOnly">
            <b-button
                    class="btn-arrow ml-auto"
                    variant="primary"
                    @click="$emit('update:disabled', false)"
            >
              Edit {{ title }}
              <font-awesome-icon
                      :icon="['far', 'arrow-right']">
              </font-awesome-icon>
            </b-button>
          </template>
          <template v-else>
            <b-button
                    v-if="!hideCancel"
                    @click="onCancel"
                    variant="outline-secondary"
            >
                Cancel
                <font-awesome-icon
                        :icon="['far', 'times']"
                ></font-awesome-icon>
            </b-button>
            <div class="ml-auto">
              <slot name="bottom-buttons"></slot>
              <b-button
                      class="btn-arrow ml-2"
                      variant="primary"
                      type="submit"
                      :disabled="saveDisabled"
              >
                Save<span class="d-none d-sm-inline"> changes</span>
                <font-awesome-icon
                        :icon="['far', 'arrow-right']"
                ></font-awesome-icon>
              </b-button>
            </div>
          </template>
        </slot>
      </div>
    </b-form>
  </div>
</template>

<script lang="js">
import { cloneDeep, forEach, isObject, isArray } from 'lodash';
import FormValidate from '@/services/Mixins/FormValidate';
import DatePicker from '@/components/Form/DatePicker';
import FileDrop from '@/components/Form/FileDrop';
const FIELD_INPUT_TYPES = [
  'color',
  'email',
  'number',
  'password',
  'range',
  'tel',
  'text',
  'time',
  'url',
];
const FIELD_DATE_TYPES = [
  'date',
];
const FIELD_TEXTAREA_TYPES = [
  'textarea',
];
const FIELD_CHECKBOX_TYPES = [
  'check',
  'checkbox',
];
const FIELD_MULTISELECT_TYPES = [
  'multi',
  'multiselect',
  'select',
];

const FIELD_BUTTON_TYPES = [
  'button',
];

const FIELD_FILE_TYPES = [
  'file',
];

export default {
  name: 'R-Form',
  components: {
    DatePicker,
    FileDrop,
  },
  mixins: [
    FormValidate,
  ],
  props: {
    value: {
      required: true,
    },
    validators: {
      default() {
        return {};
      },
    },
    title: {
      default: 'Form',
    },
    columnOptions: {
      default: () => {},
      type: Object,
    },
    disabled: {
      default: true,
      type: Boolean,
    },
    crud: {
      default: false,
      type: Boolean,
    },
    showHeader: {
      default: true,
      type: Boolean,
    },
    showFooter: {
      default: true,
      type: Boolean,
    },
    hideLoading: {
      default: false,
      type: Boolean,
    },
    hideCancel: {
      default: false,
      type: Boolean,
    },
    saveDisabled: {
      default: false,
      type: Boolean,
    },
    readOnlyDisabledMode: {
      default: false,
      type: Boolean,
    },
    uploadFiles: {
      default: false,
      type: Boolean,
    },
  },
  data() {
    return {
      originalForm: null,
      fields: [],
      readOnly: true,
      loading: false,
      readOnlyDisabled: false,
      fieldTypes: {
        checkbox: FIELD_CHECKBOX_TYPES,
        input: FIELD_INPUT_TYPES,
        multiselect: FIELD_MULTISELECT_TYPES,
        textarea: FIELD_TEXTAREA_TYPES,
        buttons: FIELD_BUTTON_TYPES,
        date: FIELD_DATE_TYPES,
        file: FIELD_FILE_TYPES,
      },
      fileRefs: [],
      fieldInteracted: {},
    };
  },
  computed: {
    valid() {
      return !this.$v.fields.$invalid;
    },
  },
  async mounted() {
    const proms = this.runOptionsFn();
    if (proms.length) {
      this.loading = true;
      await Promise.all(proms);
      this.loading = false;
    }
    this.createFileRefs();
  },
  validations() {
    const fields = {};

    // This is done so every single field can be used with
    // vuelidate's model
    forEach(this.fields, (object, key) => {
      fields[key] = {
        value: {
          required: false,
        },
      };
    });

    forEach(this.validators, (object, key) => {
      fields[key] = {
        value: object,
      };
    });

    return {
      fields,
    };
  },
  watch: {
    value: {
      async handler(newForm) {
        this.fields = newForm;

        if (!this.originalForm) {
          this.originalForm = cloneDeep(this.fields);
        }
      },
      immediate: true,
      deep: true,
    },
    valid: {
      handler(newValid) {
        this.$emit('valid', newValid);
      },
      immediate: true,
      deep: true,
    },
    disabled: {
      handler(newDisabled) {
        this.readOnly = newDisabled;

        // reset any validations on the form
        if (this.readOnly) {
          this.$v.fields.$reset();
        }

        this.readOnlyDisabled = this.readOnly && this.readOnlyDisabledMode;
      },
      immediate: true,
    },
    loading(newValue) {
      this.$emit('update:loading', newValue);
    },
    uploadFiles: {
      handler(newUploadFiles) {
        this.processFiles();
      },
      immediate: true,
    },
  },
  methods: {
    markFieldAsInteracted(key) {
        this.$set(this.fieldInteracted, key, true);
    },
    onCancel() {
      // Reset
      this.$v.fields.$reset();
      this.$emit('input', cloneDeep(this.originalForm));
      this.$emit('update:disabled', true);
      this.$emit('cancel');
    },
    onSubmit() {
      if (this.disabled) {
        return false;
      }

      if (!this.isValidated()) {
        return;
      }

      // If the type is a select, we want to set the value to be the key
      // because the value is an object, but most likely the original form wants
      // the key value.
      const optionKeys = [];
      forEach(this.fields, ((field, key) => {
        if (this.fieldTypes.multiselect.includes(field.type)
            && isObject(field.value)
            && !isArray(field.value)
        ) {
          this.fields[key].value = field.value[field.trackBy];
          optionKeys.push(key);
        }
      }));

      Object.keys(this.fields).forEach(key => {
            this.markFieldAsInteracted(key);
        });

      this.$emit('submit', this.fields);

      optionKeys.forEach((key) => {
        this.setOptionsValueToObject(key);
      });
    },
    createFileRefs() {
      const fileRefs = [];
      forEach(this.fields, (field, key) => {
        if (this.fieldTypes.file.includes(field.type)) {
          fileRefs.push(field.id + '-dropzone');
        }
      });

      this.fileRefs = fileRefs;
    },
    processFiles() {
      let hasFiles = false;

      // Loop through each this.fileRefs and call the process queue bit
      forEach(this.fileRefs, (ref, key) => {
        if (this.$refs[ref][0].hasFiles()) {
          this.$refs[ref][0].processFiles();
          hasFiles = true;
        }
      });

      if (!hasFiles) {
        this.$emit('noFilesSelected');
      }
    },
    /**
       * We check each field to see if there is an options function
       * to retrieve the options (via ajax) for stuff such as refdata
       * @param form
       * @returns {*}
       */
    runOptionsFn() {
      const arr = [];
      forEach(this.fields, (field, key) => {
        if (field.optionsFn && field.options.length === 0) {
          const promise = field.optionsFn(this.fields).then((options) => {
            this.fields[key]['options'] = options;
            this.setOptionsValueToObject(key);
          });

          arr.push(promise);
        }
      });

      return arr;
    },

    // If there is a value set, it will probably be an ID value
    // We need to insert the object from the options as the value, so lets find it!
    // trackBy is typically the key being used.
    setOptionsValueToObject(key) {
      const field = this.fields[key];
      const { value, options, trackBy } = field;

      if (value && trackBy) {
        const objectValue = options.find(item => item[trackBy] === value);
        this.fields[key]['value'] = objectValue || null;
      }
    },
    isValidated() {
      this.$v.$touch();
      return this.valid;
    },
    /**
       * Checks if there is a validation error for specific field and type
       */
    hasValidationError(key, type) {
      const field = this.fields[key];
      if (this.fieldTypes.multiselect.includes(field.type)) {
        //return false;
      }

      if (!this.$v.fields) {
        return false;
      }

      if (!this.$v.fields[key]) {
        return false;
      }

      const validationField = this.$v.fields[key].value;

      if (!validationField.$dirty) {
        return false;
      }

      if (type in validationField) {
        return validationField[type] === false;
      }
      return false;
    },
    /**
       * This is returning field values based on their type
       * Used for the read only / disabled part
       * @param field
       * @returns {*}
       */
    getFieldValue(field) {
      if (!this.fieldTypes.multiselect.includes(field.type)) {
        return field.value;
      }

      // This means it's a multi select which isn't a multiple
      // It is safe to assume that the field name will be the ID field, so we should trip that off
      if (!field.value) {
        return '';
      }

      if (isObject(field.value)) {
        return field.value[field.labelBy];
      }

      if (field.options.length > 0) {
        const v = field.options.find(opt => opt[field.trackBy] === field.value);
        return v[field.labelBy];
      }

      return field.value[field.labelBy];
    },
    /**
     * Compute the column class for the field
     * @param field
     * @returns {string}
     */
    computeColClass(field) {
      if (field.colMd) {
        return `col-md-${field.colMd} col-12 trim-col`;
      } else {
        return '';
      }
    },
    isFieldHidden(field, fields) {
      if (!field.hidden) return false;
      return typeof field.hidden === 'function' ? field.hidden(fields) : field.hidden;
    }
  },
};
</script>

<style lang="scss">
  .form-header {
    font-size: 18px;
    border-bottom: 1px solid $border-color;
    padding: 20px 15px;
    color: $purple;
  }

  .group-header {
    font-size: 18px;
    border-bottom: 1px solid $border-color;
    padding: 20px 15px;
    margin-bottom: 20px;
    color: $purple;
  }

  .form-body {
    padding-top: 20px;
    padding-bottom: 80px;
    .messages {
    }
    .vue-dropzone {
      .dz-preview {
        .dz-image {
          z-index: 0;
        }
      }
    }
  }

  .form-footer {
    border-top: 1px solid $border-color;
    padding: 20px 15px;
    display: flex;
    justify-content: space-between;
    background: $white;

    &.read-only {
      border-top-width: 0;
    }
  }

  .trim-col {
    padding: 0px;
  }

  .multiselect-is-invalid .multiselect__tags {
    border-color: #E53838;
  }

  .multiselect-is-valid .multiselect__tags {
    border-color: #5FC38D;
  }
</style>
